/**
 * Access grants — the `resource.action` vocabulary a route declares it needs
 * ([ADR 062 — Access Grants](../../../../../docs/adrs/062_STAFF_ACCESS_GRANTS.md) § 1).
 *
 * Pure, isomorphic and client-bundle-safe per
 * [ADR 010 — Isomorphic Services](../../../../../docs/adrs/010_ISOMORPHIC_SERVICES.md):
 * no I/O, no writes, no `server-only`. The same union types the registry
 * declaration on the server and (later) the nav/affordance gating on the client.
 *
 * **This file adds a vocabulary; it does not replace one.** BL-1832 is ADR 062
 * step 1A, whose whole point is to ship the enforcement *mechanism* without
 * touching the taxonomy — so today's 25 `PERMISSIONS` strings in
 * `./permissions.ts` are unchanged and remain what `requirePermission` actually
 * checks. The rename of one vocabulary into the other is step 3, and `lot.*` /
 * `bid.*` becoming first-class resources is step 4. Until then a `Grant` is
 * mapped to a `Permission` at the enforcement boundary (see
 * `grantToPermission`), which is the seam step 3 deletes.
 *
 * Why a template-literal union rather than a flat string union: the per-resource
 * action map makes `invoice.hammer` a *compile error* rather than a runtime
 * miss, because `hammer` belongs to `lot`. A flat union would accept any pair
 * someone thought to write down.
 */
import { PERMISSIONS, type Permission } from './permissions';

/**
 * The actions each resource admits. One entry per permission *resource* — the
 * ~12 nouns 47 audit resource types roll up to (ADR 062 § 2), not the audit
 * vocabulary itself.
 *
 * Tense is imperative (`void`, `send`) where audit's is past (`voided`,
 * `sent`), deliberately, so it is unambiguous which vocabulary a string belongs
 * to. The pairing between them is declared once in step 2's
 * `RESOURCE_ROLLUP` / `VERB_PAIRING`, never inferred by string identity.
 */
export type ActionsFor = {
  auction: 'read' | 'update' | 'publish' | 'approve_bidders' | 'close' | 'close_stage';
  lot:
    | 'read'
    | 'update'
    | 'hammer'
    | 'pass'
    | 'withdraw'
    | 'sequence'
    | 'reschedule'
    | 'correct_sale'
    // BL-2318: Accounting's narrow lot edit — tax, load-out fee, title paperwork.
    | 'update_accounting';
  inventory: 'read' | 'update';
  invoice: 'read' | 'create' | 'update' | 'send' | 'void';
  settlement: 'read' | 'update' | 'adjust' | 'send';
  payment: 'read' | 'create' | 'read_fees';
  customer:
    | 'read'
    | 'read_limited'
    | 'create'
    | 'update'
    | 'read_accounting'
    | 'update_documents'
    // BL-2420: ban / suspend / reactivate, and delete / restore — apart from `update`.
    | 'set_standing'
    | 'archive';
  staff: 'read' | 'invite' | 'update' | 'grant_role' | 'revoke_role';
  tenant: 'read' | 'update' | 'publish';
  file: 'read' | 'create' | 'delete';
  settings: 'read' | 'update_accounting' | 'update_auctions' | 'update_website' | 'update_support';
};

/** Every legal `resource.action` pair. `'invoice.hammer'` is not one. */
export type Grant = { [R in keyof ActionsFor]: `${R}.${ActionsFor[R]}` }[keyof ActionsFor];

/**
 * A route that genuinely needs no grant, or one whose grant has not been
 * classified yet. Carrying a non-empty reason is what keeps the escape honest —
 * a bare opt-out is indistinguishable from an oversight, and the BL-1833 sweep
 * greps these.
 *
 * **The non-empty check is at module load, not in the type.** TypeScript cannot
 * reject `''` here: every candidate template-literal spelling
 * (`` `${string}${string}` ``) still matches the empty string, and the
 * conditional-type workarounds need the literal to survive inference through a
 * nested object, which it does not. So `defineAdminRoute` throws on an empty
 * reason instead — the same enforcement point and the same failure mode as
 * `createRegistry`'s duplicate-path guard, which also runs when the registry
 * module is imported. A bad reason therefore fails the build's first import,
 * not a code review.
 */
export interface NoGrant {
  none: string;
}

/**
 * A route two roles legitimately call with no permission in common (BL-1833).
 *
 * `customers/search` is the seller picker on a Cataloger's item form AND the
 * buyer picker on Accounting's offsets; `GET auctions/[id]` feeds Admin's
 * auction pages AND Accounting's invoice modal. A single `Grant` would refuse
 * one of them, and `{ none }` would leave both tenant-only — so the caller must
 * hold ANY one of the listed grants. It composes existing grants and adds no new
 * string, which keeps it inside ADR 062 — Access Grants step 1.
 *
 * The tuple type makes a one-element list a compile error: that is just a grant
 * spelled longer, and letting it through would let the list stop meaning "more
 * than one role's permission". Duplicates are refused at module load by
 * `defineAdminRoute`, since a type cannot see them.
 */
export interface AnyOfGrant {
  anyOf: readonly [Grant, Grant, ...Grant[]];
}

/**
 * A platform route only a super admin may call (BL-1833 PR 4b).
 *
 * The platform tier — the tenant directory, custom domains, the delivery log,
 * a tenant's payment account — belongs to no tenant role (ADR 031 — Config
 * Ownership): a tenant Admin holding every grant at their own tenant is still
 * refused. None of the other shapes says that. A real grant is evaluated
 * against the caller's roles, which an Admin passes; `{ none }` lets any member
 * through. Before this shape, 18 handlers each carried their own
 * `if (!user.isSuperAdmin) return 403`.
 *
 * Like `{ none }`, it carries a reason, checked non-empty at module load.
 */
export interface SuperAdminGrant {
  superAdmin: string;
}

/**
 * What a route's `grant` field may hold: a real grant, any one of several, a
 * super-admin-only platform grant, or a reasoned opt-out.
 */
export type GrantDeclaration = Grant | AnyOfGrant | SuperAdminGrant | NoGrant;

/** Narrow a {@link GrantDeclaration} to the opt-out arm. */
export function isNoGrant(declaration: GrantDeclaration): declaration is NoGrant {
  return typeof declaration === 'object' && declaration !== null && 'none' in declaration;
}

/** Narrow a {@link GrantDeclaration} to the super-admin-only arm. */
export function isSuperAdminGrant(declaration: GrantDeclaration): declaration is SuperAdminGrant {
  return typeof declaration === 'object' && declaration !== null && 'superAdmin' in declaration;
}

/** Narrow a {@link GrantDeclaration} to the any-of arm. */
export function isAnyOfGrant(declaration: GrantDeclaration): declaration is AnyOfGrant {
  return typeof declaration === 'object' && declaration !== null && 'anyOf' in declaration;
}

/**
 * Whether `value` is a grant in the vocabulary — read off the total
 * grant → permission map, so it cannot drift from the `Grant` union.
 *
 * For declarations that reached the registry past `tsc` (BUG-2000): a typo'd or
 * retired grant behind a cast would otherwise gate every role out at request time.
 *
 * @param value - Anything a registry declaration carries as its grant.
 * @returns `true` when it is one of the `Grant` strings.
 */
export function isKnownGrant(value: unknown): value is Grant {
  return typeof value === 'string' && Object.hasOwn(GRANT_TO_PERMISSION, value);
}

/**
 * Map a `Grant` onto the `Permission` string `requirePermission` checks today.
 *
 * **This is the step-3 seam, and it is deliberately explicit rather than
 * derived.** ADR 062 step 1A ships enforcement without a taxonomy change, so
 * the two vocabularies coexist for exactly as long as steps 3–5 take; a
 * hand-written map makes every pairing reviewable and makes the eventual
 * deletion a mechanical find-and-replace. Deriving it by string munging would
 * hide the cases where the mapping is many-to-one.
 *
 * The map is TOTAL — typed `Record<Grant, Permission>`, not `Partial` — so a
 * grant added to `ActionsFor` without a permission behind it is a compile error
 * here rather than a route that silently falls back to tenant-only gating. The
 * `lot.*` grants, which have no permission of their own until ADR 062 step 4,
 * map to the permissions those routes already check today (see below).
 */
export function grantToPermission(grant: Grant): Permission {
  return GRANT_TO_PERMISSION[grant];
}

/**
 * The explicit pairing. Many-to-one is expected and correct: `invoice.send`,
 * `invoice.void` and `invoice.update` all gate on `invoices.manage` today,
 * because today's taxonomy does not distinguish them. Step 5 splits them.
 */
const GRANT_TO_PERMISSION: Record<Grant, Permission> = {
  'auction.read': PERMISSIONS.AUCTIONS_VIEW,
  'auction.update': PERMISSIONS.AUCTIONS_MANAGE,
  'auction.publish': PERMISSIONS.AUCTIONS_MANAGE,
  'auction.close': PERMISSIONS.AUCTIONS_MANAGE,
  'auction.approve_bidders': PERMISSIONS.AUCTIONS_APPROVE_BIDDERS,
  // BL-2279: its own permission, so Accounting holds it without AUCTIONS_MANAGE (BL-2283).
  'auction.close_stage': PERMISSIONS.AUCTIONS_CLOSE_STAGE,

  // `lot.*` has no permission of its own today — ADR 062 § "Feasibility" names
  // this as the largest real gap (14 audit verbs, 0 grants). Step 4 gives it
  // one; until then these routes keep gating on auctions.manage / clerking.manage
  // exactly as they do now.
  'lot.read': PERMISSIONS.AUCTIONS_VIEW,
  'lot.update': PERMISSIONS.AUCTIONS_MANAGE,
  'lot.hammer': PERMISSIONS.CLERKING_MANAGE,
  'lot.pass': PERMISSIONS.CLERKING_MANAGE,
  'lot.withdraw': PERMISSIONS.CLERKING_MANAGE,
  'lot.sequence': PERMISSIONS.CLERKING_MANAGE,
  'lot.reschedule': PERMISSIONS.CLERKING_MANAGE,
  // The post-sale corrections (sell, reverse sale, reopen, swap, adjust the
  // winning bid). They gated on `invoice.update` until BL-2119 gave them a lot
  // grant of their own; mapping to the same invoices.manage keeps exactly that
  // audience (Admin and Accounting, never a Cataloger).
  'lot.correct_sale': PERMISSIONS.INVOICES_MANAGE,
  // BL-2318: Accounting edits a lot's tax, load-out fee and title paperwork
  // (Dan Saper, 2026-10-07). Same audience as `lot.correct_sale`: Admin and
  // Accounting, never a Cataloger (who edits every field via inventory.update).
  // Field-level by necessity until ADR 062 — Access Grants step 5 decomposes
  // field grants; PATCH /api/lots/:id narrows the body to the field set
  // (`lot-edit-scope.ts`).
  'lot.update_accounting': PERMISSIONS.INVOICES_MANAGE,

  'inventory.read': PERMISSIONS.INVENTORY_VIEW,
  'inventory.update': PERMISSIONS.INVENTORY_MANAGE,

  'invoice.read': PERMISSIONS.INVOICES_VIEW,
  'invoice.create': PERMISSIONS.INVOICES_MANAGE,
  'invoice.update': PERMISSIONS.INVOICES_MANAGE,
  'invoice.send': PERMISSIONS.INVOICES_MANAGE,
  'invoice.void': PERMISSIONS.INVOICES_MANAGE,

  'settlement.read': PERMISSIONS.SETTLEMENTS_VIEW,
  'settlement.update': PERMISSIONS.SETTLEMENTS_MANAGE,
  'settlement.adjust': PERMISSIONS.SETTLEMENTS_ADJUST,
  // BL-2616 — Send to seller, Send vN and Resend: the settlement twin of `invoice.send`.
  'settlement.send': PERMISSIONS.SETTLEMENTS_MANAGE,

  'payment.read': PERMISSIONS.PAYMENTS_VIEW,
  'payment.create': PERMISSIONS.PAYMENTS_MANAGE,
  'payment.read_fees': PERMISSIONS.PAYMENT_FEES_VIEW,

  'customer.read': PERMISSIONS.CUSTOMERS_VIEW,
  'customer.read_limited': PERMISSIONS.CUSTOMERS_READ_ONLY,
  'customer.create': PERMISSIONS.CUSTOMERS_MANAGE,
  'customer.update': PERMISSIONS.CUSTOMERS_MANAGE,
  'customer.read_accounting': PERMISSIONS.CUSTOMERS_ACCOUNTING_TAB,
  // A customer's Documents tab is reachable by Accounting (the accounting
  // documents section), whose staff upload and delete there today — they hold
  // customers.accounting_tab, not customers.manage. Deleting through
  // DELETE /api/customers/:id/documents/:documentId (BL-2189) keeps exactly
  // that set: Admin and Accounting, not a Cataloger.
  'customer.update_documents': PERMISSIONS.CUSTOMERS_ACCOUNTING_TAB,
  // BL-2420: each on its own permission, so a role may one day edit customers
  // without banning or deleting them. Admin only today (Michael Lawrie, 2026-10-12).
  'customer.set_standing': PERMISSIONS.CUSTOMERS_SET_STANDING,
  'customer.archive': PERMISSIONS.CUSTOMERS_ARCHIVE,

  // The staff surface gates on settings.users today — the tenant's member
  // roster lives under Settings → Team.
  'staff.read': PERMISSIONS.SETTINGS_USERS,
  'staff.invite': PERMISSIONS.SETTINGS_USERS,
  'staff.update': PERMISSIONS.SETTINGS_USERS,
  'staff.grant_role': PERMISSIONS.SETTINGS_USERS,
  'staff.revoke_role': PERMISSIONS.SETTINGS_USERS,

  'tenant.read': PERMISSIONS.SETTINGS_VIEW,
  // A FLOOR, not the whole check: PUT /api/tenants/:id still gates each field
  // on its own settings permission (accounting / auctions; website is
  // super-admin only). settings.accounting is the one every role that can write
  // ANY tenant field holds today — Admin holds all, Accounting holds it, a
  // Cataloger holds no settings.* at all. `settings.view` was wrong twice over:
  // a read permission on a write grant, and one Accounting does not hold.
  'tenant.update': PERMISSIONS.SETTINGS_ACCOUNTING,
  'tenant.publish': PERMISSIONS.SETTINGS_WEBSITE,

  // Files inherit their parent entity's gate; the polymorphic resolver picks
  // the parent, so the grant here is the coarse "may touch attachments" one.
  'file.read': PERMISSIONS.INVENTORY_VIEW,
  'file.create': PERMISSIONS.INVENTORY_MANAGE,
  'file.delete': PERMISSIONS.INVENTORY_MANAGE,

  'settings.read': PERMISSIONS.SETTINGS_VIEW,
  'settings.update_accounting': PERMISSIONS.SETTINGS_ACCOUNTING,
  'settings.update_auctions': PERMISSIONS.SETTINGS_AUCTIONS,
  'settings.update_website': PERMISSIONS.SETTINGS_WEBSITE,
  'settings.update_support': PERMISSIONS.SETTINGS_SUPPORT,
};
